import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TeamMemberCard from './components/TeamMemberCard';
import AddTeamMemberModal from './components/AddTeamMemberModal';
import { TeamService } from '../../services/teamService';

const AboutUs = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    setLoading(true);
    try {
      const members = await TeamService?.getTeamMembers();
      setTeamMembers(members || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = (newMember) => {
    setTeamMembers(prev => [...prev, newMember]);
  };

  const handleUpdateMember = (memberId, updatedMember) => {
    setTeamMembers(prev => 
      prev?.map(member => 
        member?.id === memberId ? updatedMember : member
      )
    );
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;

    try {
      await TeamService?.deleteTeamMember(memberId);
      setTeamMembers(prev => prev?.filter(member => member?.id !== memberId));
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  const companyValues = [
    {
      icon: 'Shield',
      title: 'Trust & Reliability',
      description: 'Every team member is thoroughly vetted, insured, and committed to earning your trust through consistent, reliable service.'
    },
    {
      icon: 'Sparkles',
      title: 'Excellence in Every Detail',
      description: 'We don\'t just clean - we transform spaces with meticulous attention to detail and professional-grade techniques.'
    },
    {
      icon: 'Leaf',
      title: 'Environmental Responsibility',
      description: 'Our eco-friendly products and sustainable practices protect your family\'s health and our planet\'s future.'
    },
    {
      icon: 'Heart',
      title: 'Customer-Centric Service',
      description: 'Your satisfaction drives everything we do. We listen, adapt, and go above and beyond to exceed expectations.'
    }
  ];

  const milestones = [
    { year: '2019', event: 'Clean360 founded with a vision for better cleaning services' },
    { year: '2020', event: 'Launched eco-friendly product line and sustainable practices' },
    { year: '2021', event: 'Expanded services to cover residential and commercial cleaning' },
    { year: '2022', event: 'Introduced 24/7 emergency cleaning and advanced booking system' },
    { year: '2023', event: 'Reached 10,000+ satisfied customers across the region' },
    { year: '2024', event: 'Launched customer portal and AI-powered service matching' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 lg:px-6 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              About Clean360
            </h1>
            <p className="text-lg lg:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              We're more than a cleaning service - we're your partners in creating spaces that inspire, 
              comfort, and energize. Since 2019, we've been transforming homes and businesses with 
              professional care you can trust.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-elevation-2">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Target" size={24} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-text-primary">Our Mission</h3>
                </div>
                <p className="text-text-secondary leading-relaxed">
                  To deliver exceptional cleaning services that exceed expectations while maintaining 
                  the highest standards of professionalism, sustainability, and customer care.
                </p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-elevation-2">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Eye" size={24} className="text-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold text-text-primary">Our Vision</h3>
                </div>
                <p className="text-text-secondary leading-relaxed">
                  To become the most trusted and innovative cleaning service provider, setting new 
                  standards for excellence in the industry while protecting our environment.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-elevation-2">
                <h3 className="text-2xl font-bold text-text-primary mb-6">Why Choose Clean360?</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-text-primary">Licensed & Insured</p>
                      <p className="text-sm text-text-secondary">Full protection for your peace of mind</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-text-primary">Eco-Friendly Products</p>
                      <p className="text-sm text-text-secondary">Safe for your family and pets</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-text-primary">Satisfaction Guaranteed</p>
                      <p className="text-sm text-text-secondary">We're not happy until you are</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-text-primary">24/7 Emergency Service</p>
                      <p className="text-sm text-text-secondary">We're here when you need us most</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Company Values */}
      <section className="py-16 px-4 lg:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Our Core Values
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              These principles guide every decision we make and every service we provide.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues?.map((value, index) => (
              <div 
                key={index}
                className="text-center group hover:transform hover:scale-105 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:shadow-elevation-3 transition-shadow duration-300">
                  <Icon name={value?.icon} size={24} color="white" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">
                  {value?.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {value?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Enhanced Team Section */}
      <section className="py-20 px-4 lg:px-6 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="max-w-7xl mx-auto">
          {/* Header with Management Controls */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-primary"></div>
              <Icon name="Users" size={32} className="text-primary" />
              <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-primary"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-6 relative">
              Meet Our Leadership Team
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Dedicated professionals with decades of combined experience in delivering exceptional cleaning services.
            </p>
            
            {/* Management Controls */}
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => setIsEditMode(!isEditMode)}
                iconName={isEditMode ? "Eye" : "Edit"}
                iconPosition="left"
                className="bg-white hover:bg-primary hover:text-white"
              >
                {isEditMode ? "View Mode" : "Edit Mode"}
              </Button>
              <Button
                onClick={() => setIsAddModalOpen(true)}
                iconName="Plus"
                iconPosition="left"
                className="bg-gradient-to-r from-primary to-secondary hover:shadow-lg"
              >
                Add Team Member
              </Button>
            </div>
          </div>
          
          {/* Team Grid */}
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
            {loading ? (
              // Loading skeletons
              (Array.from({ length: 3 })?.map((_, index) => (
                <div key={index} className="bg-white rounded-3xl overflow-hidden shadow-elevation-2 animate-pulse">
                  <div className="aspect-square bg-gray-200"></div>
                  <div className="p-8 space-y-4">
                    <div className="h-6 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="h-20 bg-gray-200 rounded"></div>
                    <div className="flex space-x-2">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              )))
            ) : teamMembers?.length > 0 ? (
              teamMembers?.map((member, index) => (
                <div key={member?.id || index} className="relative group">
                  <TeamMemberCard 
                    member={member} 
                    isEditable={isEditMode}
                    onUpdate={handleUpdateMember}
                  />
                  {isEditMode && (
                    <button
                      onClick={() => handleDeleteMember(member?.id)}
                      className="absolute top-4 right-4 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  )}
                </div>
              ))
            ) : (
              // Empty state
              (<div className="col-span-full text-center py-16">
                <Icon name="Users" size={64} className="text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-4">No team members found</p>
                <Button
                  onClick={() => setIsAddModalOpen(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Your First Team Member
                </Button>
              </div>)
            )}
          </div>
        </div>
      </section>
      {/* Company Timeline */}
      <section className="py-16 px-4 lg:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Our Journey
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              From humble beginnings to industry leadership - here's how we've grown to serve you better.
            </p>
          </div>
          
          <div className="space-y-8">
            {milestones?.map((milestone, index) => (
              <div 
                key={index}
                className="flex items-start space-x-6 group"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {milestone?.year}
                  </div>
                </div>
                <div className="flex-1 pt-3">
                  <div className="bg-muted rounded-xl p-6 group-hover:bg-primary/5 transition-colors duration-300">
                    <p className="text-text-primary font-medium leading-relaxed">
                      {milestone?.event}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 px-4 lg:px-6 bg-gradient-to-r from-primary to-secondary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Experience the Clean360 Difference?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust us with their cleaning needs. 
            Let us show you what exceptional service looks like.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              iconSize={20}
              className="bg-white text-primary hover:bg-white/90"
            >
              Schedule Service
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              iconSize={20}
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              Call Now
            </Button>
          </div>
        </div>
      </section>
      {/* Add Team Member Modal */}
      <AddTeamMemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddMember}
      />
    </div>
  );
};

export default AboutUs;